// Buildings on the iPad (DD, 2026-10-06 and 26 Oct; docs/mockups/buildings-ipad.html):
// a measure holds buildings, each one building (count 1) or a building type
// measured once and repeated (count N); a building that differs from its type
// ("instance of") is one of the type's N, holds only what it adds (add-on areas)
// and always counts 1. Everything in a building's areas is priced × its count.
// The rules the screens follow, kept pure so they're tested without a device.
import type { DeviceRecord, Fields } from './device-store';

/** A top-level building with its areas, and the buildings that differ from it, each with theirs. */
export interface BuildingGroup {
  building: DeviceRecord;
  areas: DeviceRecord[];
  differing: { building: DeviceRecord; areas: DeviceRecord[] }[];
}

const order = (r: DeviceRecord): number => Number(r.fields.hig_sortorder ?? 0);
const byOrder = (a: DeviceRecord, b: DeviceRecord): number => order(a) - order(b);

/** A building's count: 1 = one building, N = a type measured once. */
export const buildingCount = (building: DeviceRecord): number => Math.max(1, Number(building.fields.hig_count) || 1);

/** The type this building differs from; null for an ordinary building or a type. */
export const typeIdOf = (building: DeviceRecord): string | null =>
  typeof building.fields.hig_instanceofid === 'string' && building.fields.hig_instanceofid ? building.fields.hig_instanceofid.toLowerCase() : null;

export const buildingName = (building: DeviceRecord): string => String(building.fields.hig_name ?? '');

/** The measure's buildings, in order. */
export function measureBuildings(records: readonly DeviceRecord[], measureId: string): DeviceRecord[] {
  return records.filter((r) => r.table === 'building' && r.parentId === measureId).sort(byOrder);
}

/** A building's areas, in order. */
function buildingAreas(records: readonly DeviceRecord[], buildingId: string): DeviceRecord[] {
  return records.filter((r) => r.table === 'area' && r.parentId === buildingId).sort(byOrder);
}

/**
 * The measure's buildings as the area list shows them: each top-level building
 * with its areas, and under it the buildings that differ from it. A building
 * whose type isn't on the device (removed elsewhere) shows at the top level.
 */
export function buildingGroups(records: readonly DeviceRecord[], measureId: string): BuildingGroup[] {
  const buildings = measureBuildings(records, measureId);
  const ids = new Set(buildings.map((b) => b.id.toLowerCase()));
  const isTopLevel = (b: DeviceRecord) => {
    const type = typeIdOf(b);
    return !type || !ids.has(type);
  };
  return buildings.filter(isTopLevel).map((building) => ({
    building,
    areas: buildingAreas(records, building.id),
    differing: buildings
      .filter((b) => !isTopLevel(b) && typeIdOf(b) === building.id.toLowerCase())
      .map((b) => ({ building: b, areas: buildingAreas(records, b.id) })),
  }));
}

/** The hours a building adds to the job: its areas' measured hours × its count. */
export function buildingHours(building: DeviceRecord, areas: readonly DeviceRecord[]): number {
  const one = areas.reduce((sum, a) => sum + (typeof a.fields.hig_measuredhours === 'number' ? a.fields.hig_measuredhours : 0), 0);
  return one * buildingCount(building);
}

/** The next free "Building N" name. */
function nextBuildingName(buildings: readonly DeviceRecord[]): string {
  const taken = new Set(buildings.map((b) => buildingName(b).trim().toLowerCase()));
  let n = buildings.length + 1;
  while (taken.has(`building ${n}`)) n++;
  return `Building ${n}`;
}

const nextOrder = (records: readonly DeviceRecord[]): number => Math.max(-1, ...records.map(order)) + 1;

/** A new building's fields: count 1, last in the list. */
export function newBuildingFields(buildings: readonly DeviceRecord[]): Fields {
  return { hig_name: nextBuildingName(buildings), hig_count: 1, hig_sortorder: nextOrder(buildings), hig_notes: null, hig_instanceofid: null };
}

/** A new building that differs from `type`: one of its count, so always 1, named for it; after the others. */
export function differingFields(type: DeviceRecord, buildings: readonly DeviceRecord[], differingSoFar: number): Fields {
  return {
    hig_name: `${buildingName(type)} – differs ${differingSoFar + 1}`,
    hig_count: 1,
    hig_sortorder: nextOrder(buildings),
    hig_notes: null,
    hig_instanceofid: type.id,
  };
}

/** A type's count can't go below 2 while it is a type, nor below the buildings that differ from it. */
export function lowestTypeCount(differing: number): number {
  return Math.max(2, differing);
}

/** Can one more building differ from this type? Only while fewer than its count already do. */
export const canAddDiffering = (type: DeviceRecord, differing: number): boolean => differing < buildingCount(type);

/** A new area's place: after every area on the job, so the job-wide order (site plan colours) stays as it was. */
export const nextAreaOrder = (areas: readonly DeviceRecord[]): number => nextOrder(areas);

/**
 * Move a top-level building one place up or down among the top-level buildings:
 * the order every top-level building then takes (0, 1, 2 …), only those that
 * change. Empty when it is already at that end.
 */
export function reorderBuildings(groups: readonly BuildingGroup[], buildingId: string, direction: -1 | 1): { id: string; order: number }[] {
  const list = groups.map((g) => g.building);
  const at = list.findIndex((b) => b.id === buildingId);
  const to = at + direction;
  if (at < 0 || to < 0 || to >= list.length) return [];
  const moved = [...list];
  [moved[at], moved[to]] = [moved[to], moved[at]];
  return moved.map((b, i) => ({ id: b.id, order: i })).filter(({ id, order: o }) => order(list.find((b) => b.id === id)!) !== o);
}

/** Why this building can't be removed from this device now; null when it can (the API checks again). */
export function buildingRemovalBlock(building: DeviceRecord, records: readonly DeviceRecord[], online: boolean): string | null {
  if (!online) return 'Removing a building needs signal.';
  if (buildingAreas(records, building.id).length) return 'Move or remove this building’s areas first.';
  if (records.some((r) => r.table === 'building' && typeIdOf(r) === building.id.toLowerCase())) return 'Remove the buildings that differ from this one first.';
  if (measureBuildings(records, building.parentId).length <= 1) return 'A job keeps at least one building. Rename this one instead.';
  if (building.state !== 'synced') return 'This building has changes still to sync. Let them sync, then remove it.';
  return null;
}

/** A building as the move picker names it: "Type 2 townhouse × 40", "Unit 12 (differs from Type 2 townhouse)". */
export function buildingLabel(building: DeviceRecord, records: readonly DeviceRecord[]): string {
  const type = typeIdOf(building);
  const typeRecord = type ? records.find((r) => r.table === 'building' && r.id.toLowerCase() === type) : undefined;
  if (typeRecord) return `${buildingName(building)} (differs from ${buildingName(typeRecord)})`;
  const count = buildingCount(building);
  return count > 1 ? `${buildingName(building)} × ${count}` : buildingName(building);
}

/** What moving an area does to its price, when its count changes; null when the count stays the same. */
export function moveWarning(areaName: string, from: DeviceRecord, to: DeviceRecord): string | null {
  const before = buildingCount(from);
  const after = buildingCount(to);
  if (before === after) return null;
  return `${areaName} is priced × ${before} in ${buildingName(from)}; in ${buildingName(to)} it will be priced × ${after}.`;
}

/**
 * Is anything of this building still on the device (an area, a building that differs from it)? A building
 * removed in CRM was empty there, so whatever is left here is this device's: an area added offline, or one
 * kept until its photos upload (#543).
 */
export const hasChildrenOnDevice = (records: readonly DeviceRecord[], buildingId: string): boolean =>
  records.some((r) => r.parentId === buildingId || (r.table === 'building' && typeIdOf(r) === buildingId.toLowerCase()));

/** On an update, the building an area's save names, so a move reaches CRM (API records.ts); other tables send no parent. */
export const updateParent = (record: Pick<DeviceRecord, 'table' | 'parentId'>): { parentId?: string } =>
  record.table === 'area' ? { parentId: record.parentId } : {};
